import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  INSIGHT_ID, IS_COMMENTABLE, ITEMS, PRIORITIZED_OPTIONS, QUESTION_ID,
  QUESTION_TYPE, RULES, SUB_TOPIC_ID, TOPIC,
} from 'constants/formFields';
import { SURVEY_ANSWER_TYPES, SURVEY_QUESTION_TYPES } from 'constants/materialAssessment';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import {
  useCreateInsightAnswersMutation, useGetStandardTopicsQuery,
  useLinkCreateMutation,
} from 'store/slices/survey/apis/surveyApi';
import getRequestDataForInsightAnswer from 'utils/formHelpers/getRequestDataForInsightAnswer';

import Modal from '../../../components/Modal/Modal';
import RegistrationModal from '../../../components/RegistrationModal/RegistrationModal';
import { AppRoutes } from '../../../constants/AppRoutes';
import { BUTTON_VARIANT } from '../../../constants/interfaces';
import useAuthCheck from '../../../hooks/useAuthCheck';
import { selectSurveyToken } from '../../../store/slices/survey/selectors';
import { useUserMeQuery } from '../../../store/slices/user/apis/user';
import useSurveyAnswerForm from './hooks/useSurveyAnswerForm';
import MASurveyAnswerView from './MASurveyAnswerView';

import type { ReactElement } from 'react';
import type { FieldErrors } from 'react-hook-form';
import type { ILinkCreateResponse } from '../../../store/slices/survey/interfaces/ISurveyRequests';
import type { TCreateInsightAnswerSchema } from '../../../utils/validators/MASurveyValidators';

type TLinkCreateMode = 'anonymous' | 'registered';

function MASurveyAnswer(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [questionData, setQuestionData] = useState<ILinkCreateResponse | undefined>(undefined);
  const [currentStep, setCurrentStep] = useState(0);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [linkCreateMode, setLinkCreateMode] = useState<TLinkCreateMode | undefined>(undefined);
  const [isInsightFinished, setInsightFinshed] = useState(false);
  const [isLeaveWarningModalOpen, setIsLeaveWarningModalOpen] = useState(false);
  const isLinkCreateRequestedRef = useRef(false);
  const { setFieldsErrors } = useFieldsErrors();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthCheck();
  const [linkCreate, { isLoading: isLoadingData, error: surveyError }] = useLinkCreateMutation();
  const [createInsight] = useCreateInsightAnswersMutation();

  const surveyToken = searchParams.get('token') || '';
  const storedSurveyToken = useSelector(selectSurveyToken);
  const isRegistrationFlowReturned = storedSurveyToken?.token === surveyToken
    && storedSurveyToken.surveyType === SURVEY_ANSWER_TYPES.INSIGHT;
  const standardId = questionData?.questionList?.standardId;

  const {
    data: standardTopicsData = [],
  } = useGetStandardTopicsQuery(standardId ? { standardId: Number(standardId) } : skipToken);

  const topLevelStandardTopicsData = useMemo(
    () => standardTopicsData.filter((topic) => topic.depth === 0),
    [standardTopicsData],
  );

  const { data: userData, isLoading: isUserDataLoading } = useUserMeQuery(isAuthenticated ? undefined : skipToken);

  const respondentId = userData?.data?.id;
  const shouldWaitForUserData = isAuthenticated && (isUserDataLoading || !respondentId);

  const {
    control,
    handleSubmit,
    setError,
    fieldsName,
    formState,
    reset,
    trigger,
  } = useSurveyAnswerForm();

  const scrollToFirstError = (errors: FieldErrors<TCreateInsightAnswerSchema>) => {
    const itemsErrors = errors[ITEMS] as Array<Record<string, unknown> | undefined> | undefined;

    if (!itemsErrors) {
      return;
    }

    const firstItemWithErrorIndex = itemsErrors.findIndex(
      (itemError) => itemError !== undefined && Object.keys(itemError).length > 0,
    );

    if (firstItemWithErrorIndex < 0) {
      return;
    }

    const questionElement = document.getElementById(`survey-question-${firstItemWithErrorIndex}`);

    if (!questionElement) {
      return;
    }

    questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const initializeFormData = useCallback((responseData: ILinkCreateResponse) => ({
    [INSIGHT_ID]: responseData.id,
    [ITEMS]: responseData.questionList.questions.map((question) => ({
      [QUESTION_ID]: question.id,
      [QUESTION_TYPE]: question.questionType,
      [IS_COMMENTABLE]: question.isCommentable,
      ...(question.questionType === SURVEY_QUESTION_TYPES.PRIORITIZE && {
        [PRIORITIZED_OPTIONS]: Array.isArray(question.options)
          ? question.options.map((option, idx) => ({
            optionId: option.id,
            priority: idx + 1,
          }))
          : [],
      }),
    })),
    [RULES]: [],
  }), []);

  useEffect(() => {
    isLinkCreateRequestedRef.current = false;
    setQuestionData(undefined);
    setLinkCreateMode(undefined);
    setIsRegisterModalOpen(false);
  }, [surveyToken]);

  useEffect(() => {
    if (!surveyToken || isAuthLoading || questionData || linkCreateMode) {
      return;
    }

    if (isRegistrationFlowReturned && isAuthenticated) {
      if (shouldWaitForUserData) {
        return;
      }

      setIsRegisterModalOpen(false);
      setLinkCreateMode('registered');
      return;
    }

    setIsRegisterModalOpen(true);
  }, [
    isAuthLoading,
    isAuthenticated,
    isRegistrationFlowReturned,
    linkCreateMode,
    questionData,
    shouldWaitForUserData,
    surveyToken,
  ]);

  useEffect(() => {
    if (
      !surveyToken
      || questionData
      || isLoadingData
      || !linkCreateMode
      || isLinkCreateRequestedRef.current
      || (linkCreateMode === 'registered' && (!isAuthenticated || shouldWaitForUserData || !respondentId))
    ) {
      return;
    }

    isLinkCreateRequestedRef.current = true;

    linkCreate({
      surveyToken,
      ...(linkCreateMode === 'registered' && respondentId ? { respondentId } : {}),
    })
      .unwrap()
      .then((response) => {
        const responseData = response.data;
        setQuestionData(responseData);
        reset(initializeFormData(responseData));
      })
      .catch(() => {
        isLinkCreateRequestedRef.current = false;
        toast.error(i18n.t('surveyAnswer.failedToLoadSurvey', 'Failed to load survey data'));
      });
  }, [
    initializeFormData,
    isAuthenticated,
    linkCreateMode,
    isLoadingData,
    linkCreate,
    questionData,
    respondentId,
    reset,
    shouldWaitForUserData,
    surveyToken,
  ]);

  const handleReview = handleSubmit(
    (values) => {
      const requestData = getRequestDataForInsightAnswer({
        values,
      });
      createInsight(requestData).unwrap()
        .then(() => {
          toast.success(i18n.t('notifications.recordHasBeenAdded'));

          if (respondentId) {
            navigate(AppRoutes.launchpad);
          } else {
            setInsightFinshed(true);
          }
        })
        .catch((errors) => {
          setFieldsErrors<TCreateInsightAnswerSchema>({ errors, setError, fieldsName });
        });
    },
    (errors) => {
      scrollToFirstError(errors);
      toast.error(i18n.t('surveyAnswer.validationError', 'Please check your answers, some fields are invalid.'));
    },
  );

  if (surveyError) {
    return (
      <div>Failed to load survey data. Please check your link and try again.</div>
    );
  }

  const handleNextStep = async () => {
    let isValid = false;

    if (currentStep === 0) {
      isValid = await trigger([INSIGHT_ID, ITEMS] as const);
    } else if (currentStep === 1) {
      isValid = await trigger([TOPIC] as const);
    } else if (currentStep === 2) {
      isValid = await trigger([SUB_TOPIC_ID] as const);
    } else if (currentStep === 3) {
      isValid = await trigger([RULES] as const);
    }

    if (isValid && currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else if (!isValid) {
      scrollToFirstError(formState.errors);
      toast.error(i18n.t('surveyAnswer.validationError', 'Please check your answers, some fields are invalid.'));
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCloseModal = () => {
    setIsRegisterModalOpen(false);
    setLinkCreateMode('anonymous');
  };

  const onCancel = () => {
    setIsLeaveWarningModalOpen(true);
  };

  const onCloseLeaveWarningModal = () => {
    setIsLeaveWarningModalOpen(false);
  };

  const onConfirmLeaving = () => {
    setIsLeaveWarningModalOpen(false);
    navigate(AppRoutes.launchpad);
  };

  const isRegistrationModalVisible = isRegisterModalOpen && !linkCreateMode && !questionData;

  return (
    <>
      {(questionData || isInsightFinished) ? (
        <MASurveyAnswerView
          control={control}
          onCancel={onCancel}
          currentStep={currentStep}
          questionData={questionData}
          standardTopicsData={topLevelStandardTopicsData}
          onNextStep={handleNextStep}
          onPrevStep={handlePrevStep}
          onReview={handleReview}
          isInsightFinished={isInsightFinished}
        />
      ) : null}
      <RegistrationModal
        open={isRegistrationModalVisible}
        surveyToken={surveyToken}
        surveyType={SURVEY_ANSWER_TYPES.INSIGHT}
        onClose={handleCloseModal}
      />
      <Modal
        open={isLeaveWarningModalOpen}
        title={t('common.leaveSurveyWarning')}
        closeTitle={t('common.continue')}
        confirmTitle={t('common.confirmLeaving')}
        confirmVariant={BUTTON_VARIANT.error}
        onReject={onCloseLeaveWarningModal}
        onConfirm={onConfirmLeaving}
        isLoading={false}
      />
    </>
  );
}

export default MASurveyAnswer;
